/**
 * Tag-normalize, href-scheme, style-declaration and escape rules shared by
 * `sanitizeInlineHtml` (server, cheerio — which additionally admits validated
 * `<span style>` via `allowStyledSpans`) and `normalizePastedHtml` (client,
 * DOM — which drops every inline style): b/i/u, a[href] (safe scheme only),
 * br. `INLINE_BLOCK_TAGS` is paste-only.
 */

export { escapeHtml as escapeInlineAttr } from "./html-entities";

export const INLINE_TAG_NORMALIZE: Record<string, "b" | "i" | "u"> = {
  b: "b",
  strong: "b",
  i: "i",
  em: "i",
  u: "u",
};

/** Block-level tags whose boundary collapses to a single `<br>` on paste. */
export const INLINE_BLOCK_TAGS = new Set([
  "p",
  "div",
  "li",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "tr",
  "blockquote",
  "section",
  "article",
  "header",
  "footer",
  "table",
  "pre",
]);

export function isSafeInlineHref(href: string): boolean {
  return /^(https?:\/\/|mailto:)/i.test(href.trim());
}

/** Raw value of `<property>:` in a `style` attribute, or null. The `(?:^|;)`
 *  anchor keeps `background-color:` from matching `color:`. */
export function styleDeclaration(
  style: string,
  property: string
): string | null {
  const re = new RegExp(`(?:^|;)\\s*${property}\\s*:\\s*([^;]+)`, "i");
  const match = re.exec(style);
  return match ? match[1].trim() : null;
}

/** Text nodes stay 3-entity (no `&quot;`, unlike `escapeHtml`) so the server sanitizer's stored output is byte-identical to before the shared home. */
export function escapeInlineText(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
